<template>
  <Teleport to="body">
    <div
      class="mtrack"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mtrack-title"
      data-tour="mall-tracking-modal"
      @keydown.esc.prevent="emit('close')"
    >
      <button type="button" class="mtrack__catch" aria-label="닫기" @click="emit('close')" />
      <div ref="panelEl" class="mtrack__panel" tabindex="-1">
        <header class="mtrack__head">
          <div>
            <h2 id="mtrack-title">배송조회</h2>
            <p class="mtrack__meta">
              <strong>{{ tracking?.productName || productName || '주문 상품' }}</strong>
              <span v-if="tracking?.orderCode || orderCode">{{ tracking?.orderCode || orderCode }}</span>
            </p>
          </div>
          <button type="button" class="mtrack__close" aria-label="닫기" @click="emit('close')">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </header>

        <div class="mtrack__ship">
          <div>
            <span>택배사</span>
            <strong>{{ tracking?.courier || courier || '-' }}</strong>
          </div>
          <div>
            <span>송장번호</span>
            <strong>{{ tracking?.invoice || invoice || '-' }}</strong>
          </div>
          <div v-if="tracking?.lastStatus">
            <span>현재 상태</span>
            <strong>{{ tracking.lastStatus }}</strong>
          </div>
        </div>

        <div class="mtrack__tools">
          <button
            type="button"
            class="mtrack__tool"
            :disabled="loading"
            @click="load"
          >
            <AppIcon icon="mdi-refresh" :size="16" />
            {{ loading ? '조회 중…' : '다시 조회' }}
          </button>
          <button
            v-if="invoiceText"
            type="button"
            class="mtrack__tool"
            @click="copyInvoice"
          >
            <AppIcon icon="mdi-content-copy" :size="16" />
            송장 복사
          </button>
          <a
            v-if="tracking?.sourceUrl"
            class="mtrack__tool"
            :href="tracking.sourceUrl"
            target="_blank"
            rel="noopener"
          >
            <AppIcon icon="mdi-open-in-new" :size="16" />
            택배사 사이트
          </a>
        </div>

        <p v-if="copied" class="mtrack__copied">송장번호를 복사했습니다.</p>
        <p v-if="tracking?.delivered" class="mtrack__copied">도착이 확인되어 주문 상태를 배송완료로 반영합니다.</p>
        <p v-if="errorMessage" class="mtrack__error">{{ errorMessage }}</p>
        <p v-else-if="tracking?.message" class="mtrack__note">{{ tracking.message }}</p>

        <div v-if="loading && !tracking" class="mtrack__empty">
          <AppIcon icon="mdi-loading" :size="24" />
          <p>배송일지를 불러오는 중입니다…</p>
        </div>

        <ol v-else-if="events.length" class="mtrack__list">
          <li
            v-for="(event, index) in events"
            :key="`${event.at}-${index}`"
            :class="{ 'is-latest': index === events.length - 1 }"
          >
            <div class="mtrack__row-top">
              <strong>{{ event.status || '처리' }}</strong>
              <time>{{ formatDateTime(event.at) }}</time>
            </div>
            <p v-if="event.location" class="mtrack__place">{{ event.location }}</p>
            <p v-if="event.description" class="mtrack__desc">{{ event.description }}</p>
          </li>
        </ol>

        <div v-else class="mtrack__empty">
          <AppIcon icon="mdi-truck-outline" :size="28" />
          <p>아직 표시할 배송일지가 없습니다.</p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { fetchOrderItemTracking, fetchPublicParcelTracking, type MallTracking } from '@/api/mallOrder'
import { copyToClipboard } from '@/utils/clipboard'

const props = defineProps<{
  itemIdx: number
  courier?: string
  invoice?: string
  productName?: string
  orderCode?: string
}>()

const emit = defineEmits<{
  close: []
}>()

const panelEl = ref<HTMLElement | null>(null)
const loading = ref(false)
const copied = ref(false)
const errorMessage = ref('')
const tracking = ref<MallTracking | null>(null)

const events = computed(() => {
  const list = [...(tracking.value?.events || [])]
  return list.sort((a, b) => {
    const left = new Date(a.at).getTime()
    const right = new Date(b.at).getTime()
    if (Number.isNaN(left) || Number.isNaN(right)) return 0
    return left - right
  })
})
const invoiceText = computed(() =>
  String(tracking.value?.invoice || props.invoice || '').replace(/\s+/g, ''),
)

function formatDateTime(value: string) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const h = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${y}.${m}.${d} ${h}:${min}`
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    const invoice = String(props.invoice || '').replace(/\s+/g, '')
    /*
     * itemIdx 가 있으면 항상 인증 엔드포인트로 간다. 서버가 이 송장을 caion_mall_order_shipment
     * 에서 찾아 개별 추적 상태(mos_track_status / mos_delivered_at) 를 저장하기 때문에,
     * 이후 화면 새로고침 시 배송완료/배송중 배지가 그대로 유지된다.
     */
    if (props.itemIdx) {
      tracking.value = await fetchOrderItemTracking(props.itemIdx, {
        courier: props.courier,
        invoice,
      })
    } else if (invoice) {
      tracking.value = await fetchPublicParcelTracking(props.courier || '', invoice)
    } else {
      errorMessage.value = '배송조회에 필요한 정보가 부족합니다.'
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '배송조회를 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

async function copyInvoice() {
  if (!invoiceText.value) return
  const ok = await copyToClipboard(invoiceText.value)
  if (!ok) {
    errorMessage.value = '송장번호 복사에 실패했습니다.'
    return
  }
  copied.value = true
  window.setTimeout(() => {
    copied.value = false
  }, 1600)
}

onMounted(() => {
  panelEl.value?.focus()
  void load()
})
</script>

<style lang="scss" src="./MallTrackingModal.scss"></style>
